import type { CategorizationRule, RuleCondition, RuleConditionGroup, RuleNode } from "../types";


// --- Nested rule group tree helpers -----------------------------------------------
// See the "Categorization rule condition tree" note in types.ts. Everything below
// treats a node without type === "group" as a leaf condition, which is what keeps
// pre-nesting saved configs working untouched.

/** Deepest group nesting the builder will let a user create (root group is depth 0). */
const MAX_RULE_GROUP_DEPTH = 5;

function isRuleGroupNode(node: RuleNode | null | undefined): node is RuleConditionGroup {
    return !!node && (node as RuleConditionGroup).type === "group";
}

/**
 * The operator joining `node` to its previous sibling: its own `joinOperator` if set,
 * otherwise the parent group's `conditionLogic`. Meaningless for the first member of a
 * list, which starts the expression and has nothing to join to.
 */
function ruleNodeJoin(node: RuleNode | null | undefined, parentLogic: "and" | "or"): "and" | "or" {
    return node?.joinOperator === "and" || node?.joinOperator === "or" ? node.joinOperator : parentLogic;
}

/**
 * Splits one level into runs of AND-joined members, implementing "AND binds tighter than
 * OR". Members that produce nothing are expected to be filtered out BEFORE this is called:
 * a dropped node takes its junction with it, and the next surviving member keeps its own
 * joinOperator (now applied to whatever precedes it).
 *
 * The first member always starts the first run - its joinOperator is never read, because
 * there is nothing to its left.
 *
 * Uniform levels collapse back to the pre-per-junction behaviour exactly:
 *   all AND -> a single run                  -> "(a AND b AND c)"
 *   all OR  -> one run per member            -> "(a OR b OR c)"
 *   1 member -> a single run of one          -> "a", bare
 */
function splitRuleAndRuns<T>(items: { value: T; join: "and" | "or" }[]): T[][] {
    if (!items.length) return [];
    const runs: T[][] = [[items[0].value]];
    for (let index = 1; index < items.length; index += 1) {
        if (items[index].join === "or") runs.push([items[index].value]);
        else runs[runs.length - 1].push(items[index].value);
    }
    return runs;
}

/** True when a level's junctions are not all the same operator, so precedence matters. */
function ruleNodesHaveMixedJoins(nodes: RuleNode[] | null | undefined, parentLogic: "and" | "or") {
    const joins = (nodes || []).slice(1).map((node) => ruleNodeJoin(node, parentLogic));
    return joins.length > 1 && joins.some((join) => join !== joins[0]);
}

/** The single operator a level uses, or null when its junctions are mixed. */
function ruleNodesUniformJoin(nodes: RuleNode[] | null | undefined, parentLogic: "and" | "or"): "and" | "or" | null {
    const joins = (nodes || []).slice(1).map((node) => ruleNodeJoin(node, parentLogic));
    if (!joins.length) return parentLogic;
    return joins.every((join) => join === joins[0]) ? joins[0] : null;
}

/**
 * Rewrites a level so every junction uses `logic`, and records it as the level's default.
 * This is what the group header's AND/OR dropdown does - a bulk setter over the level's
 * connectors. The first member is left without a joinOperator since it has no junction.
 */
function setRuleNodesUniformJoin<T extends { conditions: RuleNode[]; conditionLogic: "and" | "or" }>(container: T, logic: "and" | "or"): T {
    return {
        ...container,
        conditionLogic: logic,
        conditions: (container.conditions || []).map((node, index) => {
            if (index === 0) {
                const { joinOperator, ...rest } = node as RuleNode & { joinOperator?: "and" | "or" };
                return rest as RuleNode;
            }
            return { ...node, joinOperator: logic } as RuleNode;
        }),
    };
}

/**
 * The operator a newly appended member should join with: the level's last existing
 * junction, so appending to an all-OR level gives OR rather than silently reverting to
 * the level's stored default.
 */
function nextRuleJoinForGroup(nodes: RuleNode[] | null | undefined, parentLogic: "and" | "or"): "and" | "or" {
    const list = nodes || [];
    if (list.length < 2) return parentLogic;
    return ruleNodeJoin(list[list.length - 1], parentLogic);
}

function ruleGroupChildren(node: RuleNode | null | undefined): RuleNode[] {
    return isRuleGroupNode(node) && Array.isArray(node.conditions) ? node.conditions : [];
}

/** Every leaf condition in the tree, in document order. */
function ruleLeafConditions(nodes: RuleNode[] | null | undefined): RuleCondition[] {
    const leaves: RuleCondition[] = [];
    (nodes || []).forEach((node) => {
        if (isRuleGroupNode(node)) leaves.push(...ruleLeafConditions(node.conditions));
        else leaves.push(node as RuleCondition);
    });
    return leaves;
}

/** True when the tree contains at least one nested group (used to keep legacy output identical). */
function ruleHasNestedGroup(nodes: RuleNode[] | null | undefined): boolean {
    return (nodes || []).some((node) => isRuleGroupNode(node));
}

function ruleTreeDepth(nodes: RuleNode[] | null | undefined): number {
    return (nodes || []).reduce((deepest, node) => {
        if (!isRuleGroupNode(node)) return deepest;
        return Math.max(deepest, 1 + ruleTreeDepth(node.conditions));
    }, 0);
}

function findRuleNode(nodes: RuleNode[] | null | undefined, nodeId: string): RuleNode | null {
    for (const node of nodes || []) {
        if (node.id === nodeId) return node;
        if (isRuleGroupNode(node)) {
            const found = findRuleNode(node.conditions, nodeId);
            if (found) return found;
        }
    }
    return null;
}

/** Ids of every node on the path from the root group down to (and including) nodeId. */
function ruleNodePath(nodes: RuleNode[] | null | undefined, nodeId: string): string[] | null {
    for (const node of nodes || []) {
        if (node.id === nodeId) return [node.id];
        if (isRuleGroupNode(node)) {
            const childPath = ruleNodePath(node.conditions, nodeId);
            if (childPath) return [node.id, ...childPath];
        }
    }
    return null;
}

/** Depth of a node below the rule's root group (a direct member of the rule is 0). */
function ruleNodeDepth(nodes: RuleNode[] | null | undefined, nodeId: string): number {
    const path = ruleNodePath(nodes, nodeId);
    return path ? path.length - 1 : 0;
}

/** Immutably replaces one node anywhere in the tree. Returning null removes it. */
function mapRuleNodes(nodes: RuleNode[], nodeId: string, updater: (node: RuleNode) => RuleNode | null): RuleNode[] {
    const next: RuleNode[] = [];
    nodes.forEach((node) => {
        if (node.id === nodeId) {
            const replacement = updater(node);
            if (replacement) next.push(replacement);
            return;
        }
        if (isRuleGroupNode(node)) {
            next.push({ ...node, conditions: mapRuleNodes(node.conditions, nodeId, updater) });
            return;
        }
        next.push(node);
    });
    return next;
}

/** Immutably appends a child to the group with id groupId ("" / rule id targets the root). */
function appendRuleNode(nodes: RuleNode[], groupId: string, child: RuleNode): RuleNode[] {
    if (!groupId) return [...nodes, child];
    return nodes.map((node) => {
        if (!isRuleGroupNode(node)) return node;
        if (node.id === groupId) return { ...node, conditions: [...node.conditions, child] };
        return { ...node, conditions: appendRuleNode(node.conditions, groupId, child) };
    });
}

/** Every node id already used in this tree, so new nodes can be given collision-free ids. */
function ruleNodeIds(nodes: RuleNode[] | null | undefined, into = new Set<string>()): Set<string> {
    (nodes || []).forEach((node) => {
        if (node.id) into.add(node.id);
        if (isRuleGroupNode(node)) ruleNodeIds(node.conditions, into);
    });
    return into;
}

/**
 * Reassigns any missing or duplicated node id in a condition tree. Tree edits target a
 * node by id, so two nodes sharing an id (possible in a hand-edited or duplicated saved
 * config) would otherwise be edited and deleted together. `makeUniqueId` is expected to
 * register the id it returns.
 */
function dedupeRuleNodeIds(nodes: RuleNode[] | null | undefined, usedIds: Set<string>, makeUniqueId: (prefix: string) => string): { nodes: RuleNode[]; changed: boolean } {
    let changed = false;
    const next = (nodes || []).map((node) => {
        let current = node;
        if (!current.id || usedIds.has(current.id)) {
            changed = true;
            current = { ...current, id: makeUniqueId(isRuleGroupNode(current) ? "grp" : "cond") } as RuleNode;
        } else {
            usedIds.add(current.id);
        }
        if (isRuleGroupNode(current)) {
            const childResult = dedupeRuleNodeIds(current.conditions, usedIds, makeUniqueId);
            if (childResult.changed) {
                changed = true;
                current = { ...current, conditions: childResult.nodes };
            }
        }
        return current;
    });
    return { nodes: next, changed };
}

/** True when a group may still accept a nested child group. */
function canNestRuleGroupNodes(nodes: RuleNode[] | null | undefined, groupId: string) {
    if (!groupId) return MAX_RULE_GROUP_DEPTH > 0;
    return ruleNodeDepth(nodes, groupId) + 1 < MAX_RULE_GROUP_DEPTH;
}

function canNestRuleGroup(rule: CategorizationRule, groupId: string) {
    return canNestRuleGroupNodes(rule.conditions, groupId);
}

/**
 * Orders two sample values the way the generated SQL would: numerically when both parse as
 * numbers, then chronologically when both parse as dates, and otherwise by string order.
 * The date step is what makes the simulator agree with a Before/After condition on a
 * timestamp column - plain string ordering would get "9/2/2026" vs "10/2/2026" wrong.
 */
function compareSimValues(left: string, right: string, compare: (a: number, b: number) => boolean) {
    const leftNumber = Number(left);
    const rightNumber = Number(right);
    if (left.trim() !== "" && right.trim() !== "" && Number.isFinite(leftNumber) && Number.isFinite(rightNumber)) {
        return compare(leftNumber, rightNumber);
    }

    const leftDate = Date.parse(left);
    const rightDate = Date.parse(right);
    if (Number.isFinite(leftDate) && Number.isFinite(rightDate)) return compare(leftDate, rightDate);

    return compare(left < right ? -1 : left > right ? 1 : 0, 0);
}

/**
 * One leaf, in whichever operand mode it is in.
 *
 * `null` means "this condition is not configured enough to contribute" and the caller skips
 * it - exactly as it already does for a condition with no value typed. `false` means
 * configured but not matched. Keeping those distinct is what stops a half-built condition
 * from flipping a simulated result.
 *
 * The NULL handling below deliberately mirrors the generated SQL rather than being simpler:
 * a comparison involving a missing value is UNKNOWN in SQL and so does NOT match, EXCEPT
 * for the negated operators, whose emitted expressions carry an explicit IS NULL arm. The
 * simulator and the SQL disagreeing about blank inputs would be a bug users would find
 * with the Rule Simulator and not be able to explain.
 */
function simulateCondition(inputs: Record<string, string>, condition: RuleCondition): boolean | null {
    if (condition.leftKind === "minutes_between") {
        if (!condition.compareColumn) return null;
        const bound = (value: string | undefined) => {
            const trimmed = String(value ?? "").trim();
            return trimmed && /^-?\d+(\.\d+)?$/.test(trimmed) ? Number(trimmed) : null;
        };
        const first = bound(condition.values[0]);
        const second = bound(condition.values[1]);
        const needsTwo = condition.operator === "between" || condition.operator === "not_between";
        if (first === null || (needsTwo && second === null)) return null;

        const fromTime = Date.parse(inputs[condition.column] || "");
        const toTime = Date.parse(inputs[condition.compareColumn] || "");
        if (!Number.isFinite(fromTime) || !Number.isFinite(toTime)) {
            // DATEDIFF over a missing operand is NULL: only the negated operators keep the row.
            return condition.operator === "ne" || condition.operator === "not_between";
        }
        const raw = Math.trunc((toTime - fromTime) / 60000);
        const minutes = condition.absoluteMinutes ? Math.abs(raw) : raw;
        switch (condition.operator) {
            case "eq": return minutes === first;
            case "ne": return minutes !== first;
            case "gt": return minutes > first;
            case "gte": return minutes >= first;
            case "lt": return minutes < first;
            case "lte": return minutes <= first;
            case "between": return minutes >= first && minutes <= (second as number);
            case "not_between": return !(minutes >= first && minutes <= (second as number));
            default: return null;
        }
    }

    // "If blank, use <value>": a blank input stands in for the fallback, as ISNULL/COALESCE do
    // in the SQL. Only a BLANK input is replaced, and only when a fallback applies; otherwise the
    // input is passed through exactly as before.
    const blankValue = simBlankValue(condition);
    const inputFor = (key: string) => {
        const raw = String(inputs[key] || "");
        return blankValue !== null && !raw.trim() ? blankValue : raw;
    };

    if (condition.compareTo === "today") {
        const left = simDay(inputFor(condition.column));
        const today = localDay(new Date());
        if (!left) return condition.operator === "ne";
        switch (condition.operator) {
            case "eq": return left === today;
            case "ne": return left !== today;
            case "gt": case "after": return left > today;
            case "gte": return left >= today;
            case "lt": case "before": return left < today;
            case "lte": return left <= today;
            default: return null;
        }
    }

    if (condition.compareTo === "column") {
        if (!condition.compareColumn) return null;
        const left = inputFor(condition.column);
        const right = String(inputs[condition.compareColumn] || "");
        if (!left || !right) {
            // Either side NULL: the comparison is UNKNOWN, which only "ne" keeps.
            return condition.operator === "ne";
        }
        switch (condition.operator) {
            case "eq": return left === right;
            case "ne": return left !== right;
            case "gt": case "after": return compareSimValues(left, right, (a, b) => a > b);
            case "gte": return compareSimValues(left, right, (a, b) => a >= b);
            case "lt": case "before": return compareSimValues(left, right, (a, b) => a < b);
            case "lte": return compareSimValues(left, right, (a, b) => a <= b);
            default: return null;
        }
    }

    return simulateConditionValue(inputFor(condition.column), condition);
}

// The simulator has no column types, so it trusts the editor (which only offers a fallback the
// column can take) and mirrors only the operator rules of nullFallbackFor in sqlExpressions.ts.
// The legacy date-only blankDate keeps its original operators.
const SIM_BLANK_DATE_OPERATORS = ["eq", "ne", "gt", "gte", "lt", "lte", "before", "after", "between", "not_between"];
const SIM_FALLBACK_OPERATORS = [...SIM_BLANK_DATE_OPERATORS, "in", "not_in", "contains", "not_contains", "starts_with", "not_starts_with", "ends_with", "not_ends_with"];

function simBlankValue(condition: RuleCondition): string | null {
    if (condition.leftKind === "minutes_between") return null;
    if ((condition.compareTo === "column" || condition.compareTo === "today") && !SIM_BLANK_DATE_OPERATORS.slice(0, 8).includes(condition.operator)) return null;
    if (condition.blankValue !== undefined && condition.blankValue !== null) {
        return SIM_FALLBACK_OPERATORS.includes(condition.operator) ? condition.blankValue : null;
    }
    const value = String(condition.blankDate || "").trim();
    return /^\d{4}-\d{2}-\d{2}$/.test(value) && SIM_BLANK_DATE_OPERATORS.includes(condition.operator) ? value : null;
}

/** yyyy-mm-dd of a typed date or timestamp (by day, as the SQL compares to today), or "". */
function simDay(value: string) {
    const trimmed = value.trim();
    if (/^\d{4}-\d{2}-\d{2}/.test(trimmed)) return trimmed.slice(0, 10);
    const time = Date.parse(trimmed);
    return Number.isFinite(time) ? localDay(new Date(time)) : "";
}

function localDay(date: Date) {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

function simulateConditionValue(inputValue: string, condition: RuleCondition) {
    const value = String(inputValue || "");
    const firstValue = condition.values[0] || "";
    const lowerValue = value.toLowerCase();
    const lowerText = condition.text.toLowerCase();

    switch (condition.operator) {
        case "in": return condition.values.length ? condition.values.includes(value) : null;
        case "not_in": return condition.values.length ? !condition.values.includes(value) : null;
        case "eq": return firstValue ? value === firstValue : null;
        case "ne": return firstValue ? value !== firstValue : null;
        case "contains": return condition.text ? lowerValue.includes(lowerText) : null;
        case "not_contains": return condition.text ? !lowerValue.includes(lowerText) : null;
        case "starts_with": return condition.text ? lowerValue.startsWith(lowerText) : null;
        case "not_starts_with": return condition.text ? !lowerValue.startsWith(lowerText) : null;
        case "ends_with": return condition.text ? lowerValue.endsWith(lowerText) : null;
        case "not_ends_with": return condition.text ? !lowerValue.endsWith(lowerText) : null;
        case "gt": return firstValue ? compareSimValues(value, firstValue, (a, b) => a > b) : null;
        case "gte": return firstValue ? compareSimValues(value, firstValue, (a, b) => a >= b) : null;
        case "lt": return firstValue ? compareSimValues(value, firstValue, (a, b) => a < b) : null;
        case "lte": return firstValue ? compareSimValues(value, firstValue, (a, b) => a <= b) : null;
        case "before": return firstValue ? compareSimValues(value, firstValue, (a, b) => a < b) : null;
        case "after": return firstValue ? compareSimValues(value, firstValue, (a, b) => a > b) : null;
        case "between": {
            const upper = condition.values[1] || "";
            if (!firstValue || !upper) return null;
            const atOrAbove = compareSimValues(value, firstValue, (a, b) => a >= b);
            const atOrBelow = compareSimValues(value, upper, (a, b) => a <= b);
            return atOrAbove && atOrBelow;
        }
        case "not_between": {
            const upper = condition.values[1] || "";
            if (!firstValue || !upper) return null;
            const atOrAbove = compareSimValues(value, firstValue, (a, b) => a >= b);
            const atOrBelow = compareSimValues(value, upper, (a, b) => a <= b);
            return !(atOrAbove && atOrBelow);
        }
        case "empty": return !value.trim();
        case "not_empty": return !!value.trim();
        default: return null;
    }
}

// Mirrors ruleNodesExpression exactly, including AND-over-OR precedence via the shared
// splitRuleAndRuns: recurse into nested groups, skip nodes that contribute nothing (no
// column, no value, or an empty group) so a half-built group cannot flip a simulated
// result, then AND within each run and OR across runs.
function simulateRuleNodes(inputs: Record<string, string>, nodes: RuleNode[] | null | undefined, logic: "and" | "or"): boolean | null {
    const items = (nodes || [])
        .map((node) => {
            const result = isRuleGroupNode(node)
                ? simulateRuleNodes(inputs, node.conditions, node.conditionLogic)
                : (node as RuleCondition).column
                    ? simulateCondition(inputs, node as RuleCondition)
                    : null;
            return result === null ? null : { value: result, join: ruleNodeJoin(node, logic) };
        })
        .filter(Boolean) as { value: boolean; join: "and" | "or" }[];
    if (!items.length) return null;

    return splitRuleAndRuns(items).some((run) => run.every(Boolean));
}

function simulateRuleMatches(inputs: Record<string, string>, rule: CategorizationRule) {
    return simulateRuleNodes(inputs, rule.conditions, rule.conditionLogic) ?? false;
}

export {
    MAX_RULE_GROUP_DEPTH,
    isRuleGroupNode,
    ruleNodeJoin,
    splitRuleAndRuns,
    ruleNodesHaveMixedJoins,
    ruleNodesUniformJoin,
    setRuleNodesUniformJoin,
    nextRuleJoinForGroup,
    ruleGroupChildren,
    ruleLeafConditions,
    ruleHasNestedGroup,
    ruleTreeDepth,
    findRuleNode,
    ruleNodePath,
    ruleNodeDepth,
    mapRuleNodes,
    appendRuleNode,
    ruleNodeIds,
    dedupeRuleNodeIds,
    canNestRuleGroupNodes,
    canNestRuleGroup,
    compareSimValues,
    simulateConditionValue,
    simulateRuleNodes,
    simulateRuleMatches,
};
